import FaviconHead from '@components/head/favicons';
import HreflangHead from '@components/head/hreflangHead';
import SupportMetaTagHead from '@components/head/supportMetadata';
import { ModuleIllustration } from '@components/module/ModuleIllustration';
import { AI, COMMON, SECONDS_PER_DAY } from '@constants';
import {
    AiModuleFragment,
    ChatFragment,
    GetAiModuleByProductSlugAndLocaleDocument,
    GetAiModuleByProductSlugAndLocaleQuery,
} from '@graphql/generated/graphql';
import { Chat } from '@lib/pimster-ai-chat/components/Chat';
import { buildInitialMessage } from '@lib/pimster-ai-chat/utils/buildInitialMessage';
import { getCompanyConfig } from '@lib/uikit';
import { PageCommonProps } from '@types';
import { buildHreflangArray } from '@utils/hreflang';
import { getModuleIllustration } from '@utils/module/illustration';
import {
    withApolloClient,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodsPlugins';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface Props extends PageCommonProps {
    AIModule: Omit<AiModuleFragment, 'Status' | 'localizations'>;
}

const SupportPage: NextPage<Props> = ({
    AIModule,
    hrefLang,
    companyMeta,
    productMeta,
}: Props) => {
    const {
        locale,
        query: { initialInput },
    } = useRouter();
    const chat = AIModule.Content?.[0] as ChatFragment | undefined;

    if (!chat) return null;

    return (
        <>
            <SupportMetaTagHead title={AIModule.Title} />
            <FaviconHead productMeta={productMeta} />
            <HreflangHead
                refs={hrefLang}
                companySlug={companyMeta.Slug!}
                defaultLocale={companyMeta.DefaultLocale!}
            />
            <Chat
                agentId={chat.AgentID}
                locale={locale as string}
                name={AIModule.Title}
                assistantAvatar={
                    chat.Avatar || (
                        <ModuleIllustration
                            {...getModuleIllustration(AIModule)}
                        />
                    )
                }
                initialMessage={buildInitialMessage(chat.InitialMessage)}
                initialInput={initialInput as string | undefined}
            />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps<Props> = withErrorsGSP<Props>(
    withApolloClient<Props>(
        withCompany<Props>(
            withProduct<Props>(
                async ({ apolloClient, company, product, locale }) => {
                    const {
                        data: { modules },
                    } =
                        await apolloClient.query<GetAiModuleByProductSlugAndLocaleQuery>(
                            {
                                query: GetAiModuleByProductSlugAndLocaleDocument,
                                variables: {
                                    productSlug: product.Slug,
                                    locale,
                                },
                            }
                        );

                    const AIModule = modules?.[0];

                    if (!AIModule) return { notFound: true };

                    const { Status, localizations, ...lightAIModule } =
                        AIModule;

                    return {
                        props: {
                            AIModule: lightAIModule,
                            hrefLang: buildHreflangArray({
                                entity: AIModule,
                                currentLocale: locale!,
                                basePath: `/${product.Slug}/support`,
                            }),
                            ...(await serverSideTranslations(locale!, [
                                AI,
                                COMMON,
                            ])),
                            ...(await getCompanyConfig(
                                apolloClient,
                                company.Slug!,
                                locale!
                            )),
                        },
                        revalidate: SECONDS_PER_DAY,
                    };
                }
            )
        )
    )
);

export default SupportPage;
